import React, { FC, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import styled from 'styled-components';

import Dialog, { DialogContent } from '@crehana/ui/dist/Dialog';
import PrimaryButton from '@crehana/ui/dist/Buttons/PrimaryButton';
import { useMediaQuery } from '@crehana/react-hooks';
import { useAmplitude } from '@crehana/analytics';

import getStaticUrl from 'Jsx/Utils/getStaticUrl';

import ACTIVATE_MEMBERSHIP_MUTATION from './graphql/MarathonPopupActivateMembership.graphql';
import { MarathonPopupActivateMembership } from './graphql/types/MarathonPopupActivateMembership';

const StyledContainer = styled.div`
  max-width: 460px;
`;

const PopupImage = () => {
  const baseUrl = 'img/homepage/maraton';

  return (
    <picture>
      {/* Mobile */}
      <source
        srcSet={getStaticUrl(`${baseUrl}/popup-mobile1.webp`)}
        type="image/webp"
        media="(max-width: 767px)"
      />
      <source
        srcSet={getStaticUrl(`${baseUrl}/popup-mobile1.jpg`)}
        type="image/jpg"
        media="(max-width: 767px)"
      />
      {/* Tablet */}
      <source
        srcSet={getStaticUrl(`${baseUrl}/popup-tablet.webp`)}
        type="image/webp"
        media="(max-width: 1023px)"
      />
      <source
        srcSet={getStaticUrl(`${baseUrl}/popup-tablet.jpg`)}
        type="image/jpg"
        media="(max-width: 1023px)"
      />
      {/* Desktop */}
      <source
        srcSet={getStaticUrl(`${baseUrl}/popup-desktop.webp`)}
        type="image/webp"
        media="(min-width: 1024px)"
      />
      <source
        srcSet={getStaticUrl(`${baseUrl}/popup-desktop.jpg`)}
        type="image/jpg"
        media="(min-width: 1024px)"
      />

      <img
        src={getStaticUrl(`${baseUrl}/popup-tablet.jpg`)}
        alt="Popup background"
        className="object-contain w-full"
      />
    </picture>
  );
};

interface PopupMaratonProps {
  enable: boolean;
  pageName: 'Catalog' | 'Home User';
  title: string;
  ctaLabel: string;
}

const PopupMaraton: FC<PopupMaratonProps> = ({
  enable,
  pageName,
  title,
  ctaLabel,
}) => {
  const { matches: isSm } = useMediaQuery('sm');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [hasSeen, setHasSeen] = useState(false);
  const { logEvent } = useAmplitude();
  const [activateMembership] = useMutation<MarathonPopupActivateMembership>(
    ACTIVATE_MEMBERSHIP_MUTATION,
    {
      onCompleted({ activateMembershipSo }) {
        if (activateMembershipSo?.success) {
          window.location.reload();
        }
      },
    },
  );

  const closeDialog = () => {
    setOpen(false);
  };

  const handleCtaClick = async () => {
    setLoading(true);
    // @ts-ignore
    logEvent({
      event: `Click ${pageName} Marathon Popup Cta`,
    });

    try {
      activateMembership();
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (enable && !hasSeen) {
      setOpen(true);
      setHasSeen(true);
      // @ts-ignore
      logEvent({
        event: `Render ${pageName} Marathon Popup Cta`,
      });
    }
  }, []);

  return (
    <Dialog
      size={`${isSm ? 'm' : 's'}`}
      open={open}
      backdropWithBlur
      isDark
      onDismiss={closeDialog}
      onEscape={closeDialog}
      hideCloseBtn
      ignoreBackdropClick
    >
      <div className="relative sm:h-200 lg:h-280">
        <PopupImage />
      </div>
      <DialogContent>
        <StyledContainer className="flex flex-col items-center m-auto">
          <h3 className="font-h3 text-white text-center mb-24 sm:mb-40 sm:mt-24">
            {title}
          </h3>
          <PrimaryButton
            isDark
            as="a"
            size="l"
            onClick={handleCtaClick}
            label={ctaLabel}
            disabled={loading}
          />
        </StyledContainer>
      </DialogContent>
    </Dialog>
  );
};

export default PopupMaraton;
